/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import { MemberExperienceUserFromIdToken } from '@guildeducationinc/auth-frontend-tools';
import { LogLevel } from '@optimizely/js-sdk-logging';
import { OptimizelyProvider as Provider, createInstance } from '@optimizely/react-sdk';
import { OPTIMIZELY_BASE_URL, OPTIMIZELY_SDK_KEY } from '@src/utilities/envConfig';

/**
 * Provides access to Optimizely's APIs and client instance.
 *
 * @remarks
 * Passing the user ID allows us to get the experiment variation based on
 * configuration in Optimizely.
 */

// From the ReactSDK documentation, it looks like we can pull out the creation of the optimizely instance to outside of the functional component:
// https://www.npmjs.com/package/@optimizely/react-sdk

export interface OptimizelyUserAttributes {
	email: string | undefined;
	employer_name: string | undefined;
	employer_uuid: string | undefined;
	guild_user_id: string | undefined;
}

export const getUserOptimizelyAttributes = (
	user: MemberExperienceUserFromIdToken | undefined
): OptimizelyUserAttributes => {
	return {
		email: user?.email,
		employer_name: user?.employer,
		employer_uuid: user?.employer_uuid,
		guild_user_id: user?.id || '',
	};
};

export const optimizelyClient = createInstance({
	sdkKey: OPTIMIZELY_SDK_KEY,
	datafileOptions: {
		/**
		 * Proxy the request through Guild domains to prevent ad blocker and
		 * firewall issues.
		 */
		urlTemplate: `${OPTIMIZELY_BASE_URL}/optimizelydatafile=%s.json`,
	},
	eventBatchSize: 10,
	eventFlushInterval: 1000,
	/**
	 * @see https://docs.developers.optimizely.com/full-stack/v2.1/docs/configure-the-logger#log-levels
	 */
	logLevel: LogLevel.ERROR,
});

if (!window.optimizelyClientInstance) {
	window.optimizelyClientInstance = optimizelyClient;
}

interface OptimizelyProviderProps {
	children: React.ReactNode;
	user: MemberExperienceUserFromIdToken;
}

export const OptimizelyProvider: React.FC<OptimizelyProviderProps> = ({ children, user }) => {
	return (
		<Provider
			optimizely={optimizelyClient}
			timeout={1000}
			user={{
				id: user?.id || '',
				attributes: getUserOptimizelyAttributes(user),
			}}
		>
			{children}
		</Provider>
	);
};
